import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Divider,
  Slider,
  Alert,
  Stack
} from '@mui/material';
import { clearAllCache, getCacheStats } from '../services/cache';

export default function SettingsDialog({ open, onClose, concurrency, setConcurrency }) {
  const [customProxy, setCustomProxy] = useState('');
  const [cacheStats, setCacheStats] = useState({ count: 0, sizeKb: 0 });
  const [cacheMsg, setCacheMsg] = useState('');

  useEffect(() => {
    if (open) {
      try {
        const saved = localStorage.getItem('radio_custom_proxy') || '';
        setCustomProxy(saved);
        setCacheStats(getCacheStats());
        setCacheMsg('');
      } catch (e) {
        // ignore
      }
    }
  }, [open]);

  const handleSave = () => {
    try {
      if (customProxy.trim()) {
        localStorage.setItem('radio_custom_proxy', customProxy.trim());
      } else {
        localStorage.removeItem('radio_custom_proxy');
      }
    } catch (e) {
      // ignore
    }
    onClose();
  };

  const handleClearCache = () => {
    const cleared = clearAllCache();
    setCacheStats(getCacheStats());
    setCacheMsg(`Cleared ${cleared} cached dates successfully.`);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Settings & Preferences</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Custom Proxy Setting */}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Custom CORS Proxy
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Optionally specify your own Cloudflare Worker or CORS proxy endpoint if you do not want to rely on default public proxies.
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. https://my-proxy.workers.dev/?url="
              value={customProxy}
              onChange={(e) => setCustomProxy(e.target.value)}
              helperText="Use %s as URL placeholder, or append url query parameter"
            />
          </Box>

          <Divider />

          {/* Concurrency Setting */}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Concurrent Requests ({concurrency})
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Number of dates to fetch simultaneously. Higher values speed up multi-week scraping but may hit proxy rate limits.
            </Typography>
            <Box sx={{ px: 2 }}>
              <Slider
                value={concurrency}
                onChange={(_, val) => setConcurrency(val)}
                step={1}
                marks={[
                  { value: 1, label: '1 (Gentle)' },
                  { value: 3, label: '3 (Balanced)' },
                  { value: 5, label: '5 (Fast)' }
                ]}
                min={1}
                max={5}
                valueLabelDisplay="auto"
              />
            </Box>
          </Box>

          <Divider />

          {/* Cache Management */}
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Local Storage Cache
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Scraped dates are cached locally so past dates load instantly without re-fetching.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">
                Cached: <b>{cacheStats.count}</b> days (~{cacheStats.sizeKb} KB)
              </Typography>
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={handleClearCache}
                disabled={cacheStats.count === 0}
              >
                Clear Cache
              </Button>
            </Box>
            {cacheMsg && (
              <Alert severity="info" sx={{ mt: 1 }}>
                {cacheMsg}
              </Alert>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
